import { renderResultSvg, RESULT_CARD_SIZE, type ResultArtwork, type ResultGlyph } from "./result-card-art.ts";
import { renderNameSvg, type ResultNameArtwork } from "./result-name-art.ts";
import { resultFilename, type ResultSummary } from "./result-summary.ts";

export interface ResultCard {
  key: string;
  preview: string;
  file: File;
}

let artworkRequest: Promise<ResultArtwork> | undefined;
const nameArtworkRequests = new Map<string, Promise<ResultNameArtwork>>();

async function loadArtwork(): Promise<ResultArtwork> {
  artworkRequest ??= fetch("/social/result-art-v2.json", { signal: AbortSignal.timeout(10_000) })
    .then(async (response) => {
      if (!response.ok) throw new Error("The result artwork could not be loaded.");
      return await response.json() as ResultArtwork;
    }).catch((error: unknown) => {
      artworkRequest = undefined;
      throw error;
    });
  return artworkRequest;
}

async function loadNameArtwork(extended: boolean): Promise<ResultNameArtwork> {
  const url = extended ? "/social/result-name-extra-art-v1.json" : "/social/result-name-art-v1.json";
  let request = nameArtworkRequests.get(url);
  if (!request) {
    request = fetch(url, { signal: AbortSignal.timeout(10_000) })
      .then(async (response) => {
        if (!response.ok) throw new Error("The name handwriting could not be loaded.");
        return await response.json() as ResultNameArtwork;
      }).catch((error: unknown) => {
        nameArtworkRequests.delete(url);
        throw error;
      });
    nameArtworkRequests.set(url, request);
  }
  return request;
}

function loadSvg(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The result image could not be rendered."));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

function dataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("The result image could not be prepared."));
    reader.readAsDataURL(blob);
  });
}

async function createNameSignature(name: string): Promise<ResultGlyph> {
  const basic = await loadNameArtwork(false);
  let svg = renderNameSvg(name, basic);
  if (!svg && [...name].some((character) => character.codePointAt(0)! > 0xff && character.codePointAt(0)! <= 0x52f)) {
    const extended = await loadNameArtwork(true);
    svg = renderNameSvg(name, { ...basic, letters: { ...basic.letters, ...extended.letters } });
  }
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser cannot create a name image.");
  if (svg) {
    const image = await loadSvg(svg);
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    context.drawImage(image, 0, 0);
  } else {
    const font = "64px cursive";
    context.font = font;
    const metrics = context.measureText(name);
    const left = Math.min(0, -metrics.actualBoundingBoxLeft);
    const right = Math.max(metrics.width, metrics.actualBoundingBoxRight);
    const padding = 8;
    canvas.width = Math.ceil((right - left + padding * 2) * 2);
    canvas.height = Math.ceil((metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent + padding * 2) * 2);
    context.scale(2, 2);
    context.font = font;
    context.fillStyle = "#233f64";
    context.globalAlpha = 0.92;
    context.fillText(name, padding - left, padding + metrics.actualBoundingBoxAscent);
  }
  return { data: canvas.toDataURL("image/png"), aspect: canvas.width / canvas.height };
}

export async function createResultCard(summary: ResultSummary): Promise<ResultCard> {
  const [artwork, signature] = await Promise.all([
    loadArtwork(),
    summary.name ? createNameSignature(summary.name) : undefined,
  ]);
  const image = await loadSvg(renderResultSvg(summary, artwork, signature));
  const canvas = document.createElement("canvas");
  canvas.width = RESULT_CARD_SIZE;
  canvas.height = RESULT_CARD_SIZE;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser cannot create a result image.");
  context.drawImage(image, 0, 0, RESULT_CARD_SIZE, RESULT_CARD_SIZE);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => value ? resolve(value) : reject(new Error("The result image could not be saved.")), "image/png");
  });
  return {
    key: JSON.stringify(summary),
    preview: await dataUrl(blob),
    file: new File([blob], resultFilename(summary), { type: "image/png" }),
  };
}

export function downloadResultCard(card: ResultCard): void {
  const url = URL.createObjectURL(card.file);
  const link = document.createElement("a");
  link.href = url;
  link.download = card.file.name;
  document.body.append(link);
  try {
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}